import { z } from 'zod';
import { DecimalString, IsoDateTime, MoneyAmount } from '../primitives.js';
import { PercentCompleteMethod } from './project.js';

/**
 * S18 — Output schema for project-aggregator engine (P8) + GET /api/v1/projects/:id/metrics.
 * Frontend G10 Project Financial Dashboard consumes this directly.
 */

export const ProjectScheduleStatus = z.enum(['on_track', 'at_risk', 'overdue']);
export type ProjectScheduleStatus = z.infer<typeof ProjectScheduleStatus>;

export const ProjectCostBreakdown = z
  .object({
    labor: MoneyAmount,
    materials: MoneyAmount,
    purchase: MoneyAmount,
    landed: MoneyAmount,
    /** Cross-check value — не суммируется в total, избегаем double-count. */
    work_order_actual: MoneyAmount,
    total_actual: MoneyAmount,
  })
  .passthrough();
export type ProjectCostBreakdown = z.infer<typeof ProjectCostBreakdown>;

export const ProjectVariance = z
  .object({
    /**
     * PLM-8 (пак `tails-closeout`, B15) — `null`, когда смета в другой валюте, чем
     * проект: вычитать тенге из долларов нельзя, а до FX-нормализации честного
     * отклонения нет. Раньше вычиталось как есть, и отклонение врало.
     */
    cost_variance: MoneyAmount.nullable(),
    schedule_variance_days: z.number().int(),
    /** PLM-8 — валюта сметы ≠ валюте проекта; отклонение не посчитано. */
    currency_mismatch: z.boolean().default(false),
  })
  .passthrough();
export type ProjectVariance = z.infer<typeof ProjectVariance>;

/**
 * Z-B1 — превью процента по гипотетическому методу (без сохранения).
 *
 * Зачем отдельный контракт: в редакторе baseline PM меняет метод и должен видеть
 * «было 11,1% → станет N%». На клиенте это невычислимо — слагаемые (`progress_sum`,
 * `weighted_progress`, `weight_sum`) наружу не отдаются осознанно, и из
 * `ProjectMetrics` выводится процент ровно одного метода, текущего.
 *
 * Суммы и здесь не отдаются: наружу уходит только результат.
 */
/**
 * D2.4 (CC-3) — почему у записи превью есть `available`, а не только число.
 *
 * `task_weight` при нулевой сумме весов возвращает `'0'` — неотличимо от честного
 * «ничего не сделано». Метод в этот момент не работает, а подан как норма (CC-4).
 * Отдавать наружу сами веса нельзя (слагаемые скрыты осознанно), поэтому пригодность
 * называется структурно: `available:false` + код причины, который фронт локализует
 * сам. Без этого «disabled с причиной» на фронте было бы догадкой по числу.
 */
export const PercentPreviewUnavailableReason = z.enum([
  /** В проекте нет учитываемых листовых задач — считать не из чего. */
  'no_leaf_tasks',
  /** Листовые задачи есть, но сумма весов нулевая — взвешенный метод не работает. */
  'no_leaf_weights',
]);
export type PercentPreviewUnavailableReason = z.infer<typeof PercentPreviewUnavailableReason>;

export const ProjectPercentPreviewEntry = z
  .object({
    method: PercentCompleteMethod,
    percent_complete: DecimalString,
    /** false — метод в текущем составе задач неприменим (см. причину). */
    available: z.boolean().default(true),
    unavailable_reason: PercentPreviewUnavailableReason.nullable().default(null),
  })
  .passthrough();
export type ProjectPercentPreviewEntry = z.infer<typeof ProjectPercentPreviewEntry>;

export const ProjectPercentPreview = z
  .object({
    project_id: z.string().uuid(),
    /** Метод, сохранённый у проекта, и его процент — левая часть «было → станет». */
    current: z.object({
      method: PercentCompleteMethod,
      percent_complete: DecimalString,
    }),
    /** Запрошенный метод и процент по нему — правая часть. */
    preview: z.object({
      method: PercentCompleteMethod,
      percent_complete: DecimalString,
    }),
    /**
     * D2.4 (CC-3) — batch-режим: все запрошенные методы одним ответом.
     * Раньше список методов в форме требовал N запросов при открытии, поэтому
     * превью показывалось только у выбранного, а остальные три подписывались
     * «после сохранения пересчитается» — форма просила решение, не показывая
     * последствий. Поле опционально: одиночный вызов (`?method=`) отвечает как
     * прежде, старый клиент не ломается.
     */
    previews: z.array(ProjectPercentPreviewEntry).optional(),
  })
  // `.passthrough()`, не `.strict()`: инвариант B6/B13.3 запрещает strict в
  // `entities/**` — response-схемы обязаны переживать добавление полей на бэкенде,
  // иначе выкат BE ломает старый фронт. Проверяется `tests/invariants/zod-strict`.
  .passthrough();
export type ProjectPercentPreview = z.infer<typeof ProjectPercentPreview>;

export const ProjectMetrics = z
  .object({
    project_id: z.string().uuid(),
    computed_at: IsoDateTime,
    percent_complete: DecimalString,
    schedule_status: ProjectScheduleStatus,
    actual_start_date: IsoDateTime.nullable(),
    actual_end_date: IsoDateTime.nullable(),
    cost_breakdown: ProjectCostBreakdown,
    billed_amount: MoneyAmount,
    gross_margin: MoneyAmount,
    per_gross_margin: DecimalString,
    variance: ProjectVariance,
    /**
     * PLM-7 (пак `tails-closeout`, B18) — часть агрегатов не посчиталась (ошибка SQL).
     * Суммы таких источников в ответе — ноль-заглушка, и показывать их как факт нельзя:
     * экран обязан сказать «не посчитано». Раньше ошибка молча становилась нулём.
     */
    degraded: z.boolean().default(false),
    /** Какие источники не посчитались: `labor`, `materials`, `purchase`, `landed`, `billed`. */
    degraded_sources: z.array(z.string()).default([]),
    /**
     * PLM-1 (пак `tails-closeout`, B13; В-26) — часы без ставки: ворклоги, у которых
     * на момент записи не было оклада. В труд они не вошли; старые не пересчитываются,
     * поэтому экран обязан назвать их число, а не показать «труд 0» как факт.
     */
    labor_rate_missing_count: z.number().int().nonnegative().default(0),
    /** Сколько часов в этих ворклогах, строкой с двумя знаками. */
    labor_rate_missing_hours: DecimalString.default('0.00'),
    /** Есть часы, чья ставка в другой валюте, чем проект (сумма труда смешанная). */
    labor_currency_mixed: z.boolean().default(false),
  })
  .passthrough();
export type ProjectMetrics = z.infer<typeof ProjectMetrics>;

/**
 * PLM-6 (пак `tails-closeout`, B20; решение владельца В-5 — мягкий гейт).
 *
 * Что мешает закрыть проект. Гейт закрытия отвечал одним `closeable: false` и двумя
 * счётчиками — руководитель не видел ни неоплаченных счетов поставщиков, ни
 * непроведённых накладных расходов, ни открытых заказов. Теперь каждая причина —
 * код, число и переход к списку. Жёстко блокируют закрытие по-прежнему только
 * открытые задачи и неоплаченные счета покупателям; остальное — предупреждение.
 */
export const ProjectCloseoutBlockerCode = z.enum([
  'tasks_open',
  'ar_unpaid',
  'ap_unpaid',
  'landed_cost_open',
  'work_orders_open',
]);
export type ProjectCloseoutBlockerCode = z.infer<typeof ProjectCloseoutBlockerCode>;

export const ProjectCloseoutBlocker = z
  .object({
    code: ProjectCloseoutBlockerCode,
    count: z.number().int().positive(),
    /** Маршрут фронта, где эти записи разбираются. */
    href: z.string(),
    /** true — блокирует закрытие; false — только предупреждение (мягкий режим, В-5). */
    blocking: z.boolean(),
  })
  .passthrough();
export type ProjectCloseoutBlocker = z.infer<typeof ProjectCloseoutBlocker>;

export const ProjectCloseoutDetails = z
  .object({
    ar_unpaid_count: z.number().int().nonnegative(),
    tasks_open_count: z.number().int().nonnegative(),
    blockers: z.array(ProjectCloseoutBlocker),
  })
  .passthrough();
export type ProjectCloseoutDetails = z.infer<typeof ProjectCloseoutDetails>;

/** `GET /task-module/projects/:id/closeout` — предпросмотр гейта закрытия. */
export const ProjectCloseoutPreview = z
  .object({
    closeable: z.boolean(),
    details: ProjectCloseoutDetails,
  })
  .passthrough();
export type ProjectCloseoutPreview = z.infer<typeof ProjectCloseoutPreview>;
